import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { 
  ArrowLeft, Phone, Mail, MapPin, Tag, Edit2, Trash2,
  Briefcase, DollarSign, Calendar, X, Check, AlertCircle,
  RefreshCw, Save, Key
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ClientDetail() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchClientData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const fetchClientData = async () => {
    try {
      const [clientRes, jobsRes] = await Promise.all([
        axios.get(`${API}/clients/${clientId}`),
        axios.get(`${API}/clients/${clientId}/jobs`)
      ]);
      setClient(clientRes.data);
      setEditData(clientRes.data);
      setJobs(jobsRes.data.jobs || []);
    } catch (error) {
      toast.error('Failed to load client');
      navigate('/clients');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/clients/${clientId}`, editData);
      setClient(editData);
      setEditing(false);
      toast.success('Client updated');
    } catch (error) {
      toast.error('Failed to update client');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this client?')) return;
    
    try {
      await axios.delete(`${API}/clients/${clientId}`);
      toast.success('Client deleted');
      navigate('/clients');
    } catch (error) {
      toast.error('Failed to delete client');
    }
  };

  const handleMarkLost = async (reason) => {
    try {
      await axios.post(`${API}/clients/${clientId}/mark-lost`, { reason });
      toast.success('Client marked as lost');
      fetchClientData();
    } catch (error) {
      toast.error('Failed to update client');
    }
  };

  const handleReactivate = async () => {
    try {
      await axios.post(`${API}/clients/${clientId}/reactivate`);
      toast.success('Client reactivated');
      fetchClientData();
    } catch (error) {
      toast.error('Failed to reactivate client');
    }
  };

  const statusColors = {
    active: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    won: 'bg-blue-100 text-blue-700 border-blue-200',
    lost: 'bg-red-100 text-red-700 border-red-200'
  };

  if (loading) {
    return (
      <div className="p-4">
        <div className="h-32 bg-slate-200 rounded-xl animate-pulse mb-4" />
        <div className="h-64 bg-slate-200 rounded-xl animate-pulse" />
      </div>
    );
  }

  if (!client) return null;

  return (
    <div className="p-4 animate-fade-in" data-testid="client-detail-page">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <button
          onClick={() => navigate('/clients')}
          className="w-10 h-10 rounded-full bg-white border-2 border-slate-200 flex items-center justify-center hover:bg-slate-50"
        >
          <ArrowLeft className="w-5 h-5 text-slate-600" />
        </button>
        <div className="flex-1">
          <h1 className="font-heading text-2xl font-bold text-slate-dark">{client.name}</h1>
          <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-bold border ${statusColors[client.lead_status]}`}>
            {client.lead_status}
          </span>
        </div>
        <div className="flex gap-2">
          {!editing ? (
            <>
              <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
                <Edit2 className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={handleDelete} className="text-red-500 hover:text-red-600">
                <Trash2 className="w-4 h-4" />
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" size="sm" onClick={() => { setEditing(false); setEditData(client); }}>
                <X className="w-4 h-4" />
              </Button>
              <Button size="sm" onClick={handleSave} disabled={saving} className="bg-[#FF5F00]">
                <Save className="w-4 h-4" />
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Lost Reason Alert */}
      {client.lead_status === 'lost' && client.lost_reason && (
        <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 mb-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-medium text-red-700">Lost Reason</p>
            <p className="text-red-600">{client.lost_reason}</p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleReactivate}
            className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"
          >
            <RefreshCw className="w-4 h-4 mr-1" /> Reactivate
          </Button>
        </div>
      )}

      {/* Client Info Card */}
      <div className="bg-white rounded-xl border-2 border-slate-200 shadow-hard p-4 mb-4">
        {editing ? (
          <div className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-1 block">Name</label>
              <Input
                value={editData.name || ''}
                onChange={(e) => setEditData({ ...editData, name: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-1 block">Phone</label>
                <Input
                  value={editData.phone || ''}
                  onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-1 block">Email</label>
                <Input
                  type="email"
                  value={editData.email || ''}
                  onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-1 block">Street</label>
              <Input
                value={editData.address_street || ''}
                onChange={(e) => setEditData({ ...editData, address_street: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-1 block">City</label>
                <Input
                  value={editData.address_city || ''}
                  onChange={(e) => setEditData({ ...editData, address_city: e.target.value })}
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-1 block">State</label>
                <Input
                  value={editData.address_state || ''}
                  onChange={(e) => setEditData({ ...editData, address_state: e.target.value })}
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-1 block">Zip</label>
                <Input
                  value={editData.address_zip || ''}
                  onChange={(e) => setEditData({ ...editData, address_zip: e.target.value })}
                />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-1 block">Notes</label>
              <textarea
                value={editData.notes || ''}
                onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                className="w-full px-3 py-2 border-2 border-slate-200 rounded-lg resize-none h-20"
              />
            </div>
            {/* Access Details in Edit Mode */}
            <div className="border-t border-slate-200 pt-4">
              <p className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-3">Access Details</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-1 block">Gate Code</label>
                  <Input
                    value={editData.gate_code || ''}
                    onChange={(e) => setEditData({ ...editData, gate_code: e.target.value })}
                    placeholder="#1234"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-1 block">Lockbox Code</label>
                  <Input
                    value={editData.lockbox_code || ''}
                    onChange={(e) => setEditData({ ...editData, lockbox_code: e.target.value })}
                    placeholder="5678"
                  />
                </div>
              </div>
              <div className="mt-3">
                <label className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-1 block">Access Notes</label>
                <Input
                  value={editData.access_notes || ''}
                  onChange={(e) => setEditData({ ...editData, access_notes: e.target.value })}
                  placeholder="e.g., Side gate, ring doorbell twice..."
                />
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {client.phone && (
              <a href={`tel:${client.phone}`} className="flex items-center gap-3 text-slate-700 hover:text-[#FF5F00]">
                <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                  <Phone className="w-5 h-5 text-slate-500" />
                </div>
                <span className="font-medium">{client.phone}</span>
              </a>
            )}
            {client.email && (
              <a href={`mailto:${client.email}`} className="flex items-center gap-3 text-slate-700 hover:text-[#FF5F00]">
                <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-slate-500" />
                </div>
                <span className="font-medium">{client.email}</span>
              </a>
            )}
            {client.full_address && (
              <div className="flex items-center gap-3 text-slate-700">
                <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-slate-500" />
                </div>
                <span>{client.full_address}</span>
              </div>
            )}
            {client.notes && (
              <div className="pt-3 border-t border-slate-100">
                <p className="text-xs uppercase tracking-wider font-bold text-slate-400 mb-1">Notes</p>
                <p className="text-slate-600">{client.notes}</p>
              </div>
            )}
            {client.tags && client.tags.length > 0 && (
              <div className="pt-3 border-t border-slate-100">
                <p className="text-xs uppercase tracking-wider font-bold text-slate-400 mb-2">Tags</p>
                <div className="flex flex-wrap gap-1">
                  {client.tags.map((tag, idx) => (
                    <span key={idx} className="px-2 py-1 bg-slate-100 rounded-full text-sm">
                      <Tag className="w-3 h-3 inline mr-1" />{tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {/* Access Details Display */}
            {(client.gate_code || client.lockbox_code || client.access_notes) && (
              <div className="pt-3 border-t border-slate-100">
                <p className="text-xs uppercase tracking-wider font-bold text-slate-400 mb-2">Access Details</p>
                <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <div className="flex items-start gap-2">
                    <Key className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="space-y-1">
                      {client.gate_code && (
                        <p className="text-sm text-amber-800"><strong>Gate Code:</strong> {client.gate_code}</p>
                      )}
                      {client.lockbox_code && (
                        <p className="text-sm text-amber-800"><strong>Lockbox:</strong> {client.lockbox_code}</p>
                      )}
                      {client.access_notes && (
                        <p className="text-sm text-amber-800"><strong>Notes:</strong> {client.access_notes}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="bg-white rounded-xl border-2 border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
              <Briefcase className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-dark">{jobs.length}</p>
              <p className="text-sm text-slate-500">Total Jobs</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border-2 border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-emerald-600">
                ${jobs.filter(j => j.status === 'paid').reduce((sum, j) => sum + (j.quote_amount || 0), 0).toLocaleString()}
              </p>
              <p className="text-sm text-slate-500">Revenue</p>
            </div>
          </div>
        </div>
      </div>

      {/* Lead Actions */}
      {client.lead_status !== 'lost' && (
        <div className="bg-white rounded-xl border-2 border-slate-200 p-4 mb-4">
          <p className="text-xs uppercase tracking-wider font-bold text-slate-400 mb-3">Lead Actions</p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              className="flex-1 text-red-500 border-red-200 hover:bg-red-50"
              onClick={() => {
                const reason = prompt('Why did you lose this client?');
                if (reason) handleMarkLost(reason);
              }}
            >
              <X className="w-4 h-4 mr-1" /> Mark as Lost
            </Button>
            {client.lead_status === 'active' && (
              <Button
                variant="outline"
                className="flex-1 text-blue-600 border-blue-200 hover:bg-blue-50"
                onClick={async () => {
                  try {
                    await axios.put(`${API}/clients/${clientId}`, { lead_status: 'won' });
                    toast.success('Client marked as won!');
                    fetchClientData();
                  } catch (e) {
                    toast.error('Failed to update');
                  }
                }}
              >
                <Check className="w-4 h-4 mr-1" /> Mark as Won
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Job History */}
      <div className="bg-white rounded-xl border-2 border-slate-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <p className="font-heading text-lg font-bold text-slate-dark">Job History</p>
          <Button
            size="sm"
            onClick={() => navigate('/new-job', { state: { client } })}
            className="bg-[#FF5F00] hover:bg-orange-600"
          >
            New Job
          </Button>
        </div>
        
        {jobs.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <Briefcase className="w-12 h-12 mx-auto mb-2 opacity-30" />
            <p>No jobs yet</p>
          </div>
        ) : (
          <div className="space-y-2">
            {jobs.map((job) => (
              <div
                key={job.id}
                onClick={() => navigate(`/job/${job.id}`, { state: { fromClient: true, clientId: client.id } })}
                className="p-3 rounded-lg border border-slate-200 hover:border-[#FF5F00] cursor-pointer transition-colors"
              >
                <div className="flex items-center justify-between mb-1">
                  <p className="font-medium text-slate-dark truncate">{job.description || 'Untitled Job'}</p>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                    job.status === 'paid' ? 'bg-emerald-100 text-emerald-700' :
                    job.status === 'scheduled' ? 'bg-amber-100 text-amber-700' :
                    'bg-blue-100 text-blue-700'
                  }`}>
                    {job.status}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm text-slate-500">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {job.scheduled_date 
                      ? new Date(job.scheduled_date).toLocaleDateString() 
                      : new Date(job.created_at).toLocaleDateString()}
                  </span>
                  <span className="font-medium text-slate-dark">
                    ${(job.quote_amount || 0).toFixed(2)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
